// ─────────────────────────────────────────────────────────
// Configurator reducer.
//
// Extracted from the provider so the state transitions that matter — colour
// mode, downstream invalidation — can be tested directly. The provider in
// store/configurator.tsx is the only consumer.
// ─────────────────────────────────────────────────────────

import {
  type ConfiguratorState,
  type ConfiguratorString,
  CONFIGURATOR_INITIAL_STATE,
} from "@/types/configurator";

// ─────────────────────────────────────────────────────────
// Actions
// ─────────────────────────────────────────────────────────

export type ConfiguratorAction =
  | { type: "SET_INSTRUMENT"; instrumentId: string }
  | { type: "SET_STRING_COUNT"; stringCountId: string; count: number; isNonStandard: boolean }
  | { type: "SET_SCALE_LENGTH"; scaleLengthId: string }
  | { type: "SET_LINE"; lineId: string }
  | { type: "SET_STRING_TYPE"; stringTypeId: string }
  | { type: "SET_PRESET"; presetId: string; strings: ConfiguratorString[]; presetName?: string | null }
  | { type: "UPDATE_STRING"; position: number; updates: Partial<ConfiguratorString> }
  | { type: "SET_STRINGS"; strings: ConfiguratorString[] }
  | { type: "SET_COLOR"; colorId: string }
  | { type: "SET_STRING_COLOR"; position: number; colorId: string }
  | { type: "TOGGLE_PER_STRING_COLOR"; enabled: boolean }
  | { type: "RESET_STRING_COLORS" }
  | { type: "SET_SET_NAME"; name: string }
  | { type: "SET_QUANTITY"; quantity: number }
  | { type: "SET_PRICES"; unitPrice: number; totalPrice: number }
  | { type: "RESET" };

export function configuratorReducer(state: ConfiguratorState, action: ConfiguratorAction): ConfiguratorState {
  switch (action.type) {
    case "SET_INSTRUMENT":
      // Reset downstream when instrument changes
      return {
        ...CONFIGURATOR_INITIAL_STATE,
        instrumentId: action.instrumentId,
      };

    case "SET_STRING_COUNT":
      return {
        ...state,
        stringCountId: action.stringCountId,
        stringCount: action.count,
        isNonStandard: action.isNonStandard,
        // Reset gauge/color selections when count changes
        presetId: null,
        basePresetName: null,
        strings: [],
        isCustomGauge: false,
      };

    case "SET_SCALE_LENGTH":
      return { ...state, scaleLengthId: action.scaleLengthId };

    case "SET_LINE":
      return {
        ...state,
        lineId: action.lineId,
        // Reset string type + gauge/color when line changes
        stringTypeId: null,
        presetId: null,
        basePresetName: null,
        strings: [],
        isCustomGauge: false,
        colorId: null,
      };

    case "SET_STRING_TYPE":
      return {
        ...state,
        stringTypeId: action.stringTypeId,
        // Reset gauge/color when string type changes — same shape as SET_LINE
        presetId: null,
        basePresetName: null,
        strings: [],
        isCustomGauge: false,
        colorId: null,
      };

    case "SET_PRESET":
      return {
        ...state,
        presetId: action.presetId,
        basePresetName: action.presetName ?? null,
        strings: action.strings,
        isCustomGauge: false,
      };

    case "UPDATE_STRING": {
      const updated = state.strings.map((s) =>
        s.position === action.position ? { ...s, ...action.updates } : s
      );
      // Editing a gauge or a winding leaves the preset behind. Keeping
      // presetId here claimed the catalog set was unchanged, and the server
      // rejected the quote because the strings no longer matched it. Colour
      // and quantity edits are not specification changes, so they keep the
      // preset reference. basePresetName is display-only provenance
      // ("Modified from Regular Light"); it is never a preset identity.
      const specificationChanged =
        action.updates.gaugeValue !== undefined || action.updates.windingType !== undefined;
      if (!specificationChanged) return { ...state, strings: updated };
      return {
        ...state,
        strings: updated,
        presetId: null,
        isCustomGauge: true,
      };
    }

    case "SET_STRINGS":
      // Strings supplied without a preset are a custom build by definition —
      // the seeded curve when a line carries no set for this count, and a
      // restored URL that carried no preset reference.
      return {
        ...state,
        strings: action.strings,
        presetId: null,
        basePresetName: null,
        isCustomGauge: true,
      };

    case "SET_COLOR":
      // In per-string mode this is the base color for unset strings —
      // keep the mode and the per-string overrides intact.
      if (state.isPerStringColor) {
        return { ...state, colorId: action.colorId };
      }
      return {
        ...state,
        colorId: action.colorId,
        // Uniform mode: every string inherits the set colour.
        strings: state.strings.map((s) => ({ ...s, colorId: null })),
        isPerStringColor: false,
        stringColorDraft: null,
      };

    case "SET_STRING_COLOR": {
      const updated = state.strings.map((s) =>
        s.position === action.position ? { ...s, colorId: action.colorId } : s
      );
      // Always keep per-string mode active once the user explicitly picks per-string colors.
      // (Previously this toggled off when all strings happened to match the set color — a confusing bug.)
      return { ...state, strings: updated, isPerStringColor: true };
    }

    case "TOGGLE_PER_STRING_COLOR":
      // Turning the switch off used to wipe every per-string colour, so
      // flicking it twice silently destroyed the customer's work. The draft is
      // kept instead: off hides it, on brings it back. Discarding it is an
      // explicit, separate action (RESET_STRING_COLORS).
      if (!action.enabled) {
        return {
          ...state,
          isPerStringColor: false,
          stringColorDraft: state.strings.map((s) => ({
            position: s.position,
            colorId: s.colorId,
          })),
          strings: state.strings.map((s) => ({ ...s, colorId: null })),
        };
      }
      return {
        ...state,
        isPerStringColor: true,
        strings: state.strings.map((s) => ({
          ...s,
          colorId:
            state.stringColorDraft?.find((d) => d.position === s.position)?.colorId ??
            s.colorId,
        })),
      };

    case "RESET_STRING_COLORS":
      // Explicit discard: every string falls back to the set colour.
      return {
        ...state,
        stringColorDraft: null,
        strings: state.strings.map((s) => ({ ...s, colorId: null })),
      };

    case "SET_SET_NAME":
      return { ...state, setName: action.name };

    case "SET_QUANTITY":
      return { ...state, quantity: action.quantity };

    case "SET_PRICES":
      return { ...state, unitPrice: action.unitPrice, totalPrice: action.totalPrice };

    case "RESET":
      return { ...CONFIGURATOR_INITIAL_STATE };

    default:
      return state;
  }
}

